import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { SCHEMAS } from "./schemas";
import { RecordForm } from "./RecordForm";

const cell = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (v == null) return "—";
  return String(v);
};

export const CrudManager = ({ collection }) => {
  const schema = SCHEMAS[collection];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/data/${collection}`); setRows(data); }
    catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection]);

  const save = async (payload) => {
    try {
      await api.post(`/admin/crud/${collection}`, payload);
      toast.success(`${schema.singular} saved`);
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async () => {
    try { await api.delete(`/admin/crud/${collection}/${confirm.id}`); toast.success("Deleted"); setConfirm(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid={`crud-${collection}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-4xl text-white">{schema.label}</h1>
        <Button onClick={() => { setEditing({}); setOpen(true); }} className="btn-primary rounded-lg" data-testid={`crud-add-${collection}`}>
          <Plus size={15} className="mr-2" />New {schema.singular}
        </Button>
      </div>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>}
      {!loading && rows.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No records yet. Add your first {schema.singular.toLowerCase()}.</div>}
      {!loading && rows.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  {schema.columns.map((c) => <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c.replace("_", " ")}</th>)}
                  <th className="p-3 text-right text-[10px] font-mono uppercase tracking-widest text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`crud-row-${r.id}`}>
                    {schema.columns.map((c) => <td key={c} className="p-3 font-mono text-xs text-slate-300 max-w-[220px] truncate">{cell(r[c])}</td>)}
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setEditing(r); setOpen(true); }} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid={`crud-edit-${r.id}`}><Pencil size={14} /></button>
                        <button onClick={() => setConfirm(r)} className="p-2 rounded-lg hover:bg-rose-500/15 text-rose-400" data-testid={`crud-del-${r.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RecordForm open={open} onClose={() => { setOpen(false); setEditing(null); }} schema={schema} record={editing} onSave={save} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="glass-strong border-white/10" data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {schema.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes “{confirm?.name || confirm?.title || confirm?.sku || confirm?.coa_id || confirm?.id}”. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-500" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
